import { Grid, Typography, Chip, SxProps, Theme, Avatar } from '@mui/material';
import useSyndicates from '../syndicates/useSyndicates';
import { Syndicate } from '../models/syndicate';
import useShallowRounds from '../rounds/useShallowRounds';
import { getNumFixtures } from '../common/roundhelpers';
import TerminalSyndicate from './terminalsyndicate';
import terminalBg from '../images/dbet_terminal_background.png';
import { useState, useEffect } from 'react';
import useAgents from '../syndicates/useAgents';
import fetchApi from '../backend/fetchApi';
import { useTranslation } from 'react-i18next';

export default function TerminalAgent({ agentId }: { agentId: number }) {
  
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  const syndicates = useSyndicates();
  const agents = useAgents();
  const shallowRounds = useShallowRounds(true, false);
  const { t } = useTranslation();

  let agent = agents?.find(a => a.id === agentId);

  const fetchImage = async (imageUrl: string) => {
    try {
      const response = await fetchApi(imageUrl);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      return objectUrl;
    } catch (error) {
      console.error("Error fetching image:", error);
      return null;
    }
  };

  useEffect(() => {
    async function loadAvatar() {
      if (agent?._links?.avatarImage?.href) {
        const url = await fetchImage(agent._links.avatarImage.href);
        if (url) {
          setAvatarUrl(url);
        }
      }
    }

    if (agent) {
      loadAvatar();
    }
  }, [agent]);

  const chipStyle: SxProps<Theme> = {
    color: '#FFFFFF',
    fontSize: '20px',
    fontFamily: 'Roboto',
    fontWeight: '700',
    padding: '5px 17px',
    borderRadius: '64px',
    height: '45px',

    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#4075FF'
    }
  };

  const getChipBackgroundColor = (filterName: string) => {
    return selectedFilter === filterName ? '#4075FF' : '#112F7C';
  };

  let list: Syndicate[] = [];

  if (syndicates[0] !== undefined) {
    list = syndicates[0].filter(s => s.agentId === agentId);
  }

  if (shallowRounds[0] === undefined)
    return <></>;

  list.sort((a, b) => {
    let roundA = shallowRounds[0]?.rounds?.find(r => r.id === a.roundId.toString());
    let roundB = shallowRounds[0]?.rounds?.find(r => r.id === b.roundId.toString());

    if (!roundA || !roundB) return 0; // If either round missing, keep order

    // Convert openTo to Date (default to now if missing)
    const dateA = new Date(roundA.openTo ?? Date.now());
    const dateB = new Date(roundB.openTo ?? Date.now());

    // --- Adjust "day boundary" to 9 AM ---
    const shiftMs = 9 * 60 * 60 * 1000; // 9 hours in ms
    const shiftedA = new Date(dateA.getTime() - shiftMs);
    const shiftedB = new Date(dateB.getTime() - shiftMs);

    // Get date only, after shifting
    const aDateOnly = new Date(shiftedA.getFullYear(), shiftedA.getMonth(), shiftedA.getDate()).getTime();
    const bDateOnly = new Date(shiftedB.getFullYear(), shiftedB.getMonth(), shiftedB.getDate()).getTime();

    // Compare by "day" (with 9 AM as boundary)
    if (aDateOnly !== bDateOnly) return aDateOnly - bDateOnly;

    // If same adjusted day, compare by fixture count
    const fixturesA = getNumFixtures(roundA.name);
    const fixturesB = getNumFixtures(roundB.name);
    if (fixturesA !== fixturesB) {
      return fixturesB - fixturesA;
    }

    // If same fixture count, compare by actual time of day
    if (dateA.getTime() !== dateB.getTime()) {
      return dateA.getTime() - dateB.getTime();
    }

    // If same round, compare by stake amount
    if (roundA.id === roundB.id) {
      return Number(b.stakePerShare.amount) - Number(a.stakePerShare.amount);
    }

    return 0; // Equal for sorting purposes
  });

  list = list.sort((a, b) => {
    const diff1 = a.targetNShares - a.nShares;
    const diff2 = b.targetNShares - b.nShares;

    // Objects where a - b = 0 should come last
    if (diff1 === 0 && diff2 !== 0) return 1;
    if (diff2 === 0 && diff1 !== 0) return -1;
    return 0; // Keep relative order for the rest
  })

  // Apply filter based on selectedFilter
  let filteredList = list.filter((syndicate) => {
    const round = shallowRounds[0]?.rounds?.find(r => r.id === syndicate.roundId.toString() &&
      r.status === "ACTIVE" && new Date(r.openTo) > new Date(Date.now()));

    if (!round) return false;
    return true;
  });

  if (selectedFilter) {
    filteredList = filteredList.filter((syndicate) => {
      const round = shallowRounds[0]?.rounds?.find(r => r.id === syndicate.roundId.toString());
      if (!round) return false;

      switch (selectedFilter) {
        case 'super8':
          return round.name.toLowerCase().includes('flex');
        case 'super12':
          return round.name.toLowerCase().includes('mix');
        case 'super13':
          return round.name.toLowerCase().includes('max');
        case 'hockey9':
          return round.name.toLowerCase().includes('hockey');
        default:
          return true;
      }
    });
  }

  return (

    <Grid container direction="column" sx={{
      backgroundImage: `url(${terminalBg})`,
      backgroundSize: 'cover',
      backgroundPosition: 'top center',
      backgroundRepeat: 'no-repeat',
      backgroundColor: 'transparent',
      padding: "40px",
      alignItems: "flex-start",
      width: "100%",
      minHeight: "100vh",
      overflow: "hidden",
      position: "relative"
    }}>

      {/* Header Row */}
      <Grid container direction="row" alignItems="center" justifyContent="space-between" sx={{
        width: "100%",
        marginBottom: "20px",
        flexShrink: 0,
        flexWrap: "nowrap"
      }}>
        <Grid item sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          {avatarUrl ? (
            <Avatar alt={agent?.name} src={avatarUrl} sx={{ width: 56, height: 56, marginRight: "16px" }} />
          ) : (
            <Avatar sx={{ width: 56, height: 56, marginRight: "16px", bgcolor: "#1F4BA8", color: "#FFFFFF", fontSize: "24px", fontWeight: "700" }}>
              {agent?.name?.[0]}
            </Avatar>
          )}
          <Typography sx={{
            fontSize: "46px",
            fontWeight: "700",
            color: "#091F51",
            fontFamily: 'Roboto',
            textTransform: "uppercase",
            fontStyle: "italic",
          }}>
            {t('syndicates')}
          </Typography>
        </Grid>
      </Grid>

      {/* Round type filters */}
      <Grid item sx={{
        width: "100%",
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "12px",
        marginBottom: "20px",
        flexShrink: 0
      }}>
        <Chip
          label="Alla"
          onClick={() => setSelectedFilter(null)}
          sx={{
            backgroundColor: selectedFilter === null ? '#5B7FFF' : '#1F4BA8',
            ...chipStyle
          }}
        />
        <Chip
          label="Super 8"
          onClick={() => setSelectedFilter('super8')}
          sx={{
            backgroundColor: getChipBackgroundColor('super8'),
            ...chipStyle
          }}
        />
        <Chip
          label="Super 12"
          onClick={() => setSelectedFilter('super12')}
          sx={{
            backgroundColor: getChipBackgroundColor('super12'),
            ...chipStyle
          }}
        />
        <Chip
          label="Super 13"
          onClick={() => setSelectedFilter('super13')}
          sx={{
            backgroundColor: getChipBackgroundColor('super13'),
            ...chipStyle
          }}
        />
        <Chip
          label="Hockey 9"
          onClick={() => setSelectedFilter('hockey9')}
          sx={{
            backgroundColor: getChipBackgroundColor('hockey9'),
            ...chipStyle
          }}
        />
      </Grid>

      {/* Syndicates List */}
      <Grid container direction="column" sx={{ width: "100%", alignItems: "center", overflow: "hidden" }}>
        {filteredList.map((syndicate, i) => {

          let round = shallowRounds[0]?.rounds?.find(r => r.id === syndicate.roundId.toString());

          return <TerminalSyndicate key={"syndicate" + i} syndicate={syndicate} round={round!} />
        })}
      </Grid>

    </Grid >
  );
}

